import { Card, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Cpu, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

/**
 * PillarPage — single reusable template for every WarehouseX pillar page
 * that mirrors the layout of the existing `Home` (SmartStrategy) page.
 *
 * Rules (per product spec):
 *  - Do NOT modify the existing SmartStrategy page (HeroSection / ModuleCards).
 *  - Reuse the SAME layout, typography, spacing, card structure, CTA style.
 *  - No status badges, no Coming Soon, no disabled/greyed-out styling.
 *  - No animations beyond the shared hover-shadow used by Shadcn Card.
 *
 * Props let each concrete page plug in its own title / description /
 * supporting text / bottom bullet / hero image / modules array.
 */
export default function PillarPage({
  title,
  description,
  supportText,
  bulletText,
  heroImage,
  heroAlt,
  modules,
  testIdPrefix = "pillar",
}) {
  const navigate = useNavigate();
  const gridCols =
    modules.length === 4
      ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-2"
      : "grid-cols-1 md:grid-cols-3";

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1">
        {/* HERO ---------------------------------------------------------- */}
        <section
          data-testid={`${testIdPrefix}-hero-section`}
          className="bg-[#F8FAFC] border-b border-[#E5E7EB]"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-1.5 bg-white border border-[#E5E7EB] rounded-sm px-2.5 py-1 mb-4">
                  <Cpu className="w-3.5 h-3.5 text-[#025979]" strokeWidth={1.5} />
                  <span className="text-[11px] font-medium text-[#025979] tracking-wide">
                    WarehouseX Platform
                  </span>
                </div>
                <h1
                  data-testid={`${testIdPrefix}-hero-title`}
                  className="text-4xl sm:text-5xl font-bold tracking-tight text-[#111827] leading-[1.05]"
                  style={{ fontFamily: "Chivo, sans-serif" }}
                >
                  {title}
                </h1>
                <p
                  data-testid={`${testIdPrefix}-hero-subtitle`}
                  className="mt-4 text-base sm:text-lg text-[#6B7280] leading-relaxed max-w-lg"
                >
                  {description}
                </p>
                <p
                  data-testid={`${testIdPrefix}-hero-support-text`}
                  className="mt-3 text-sm text-[#374151] max-w-lg"
                >
                  {supportText}
                </p>
                <div className="mt-5 flex items-center gap-2 text-xs text-[#6B7280]">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-pulse-subtle absolute inline-flex h-full w-full rounded-full bg-[#025979] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#025979]"></span>
                  </span>
                  <span>{bulletText}</span>
                </div>
              </div>
              <div className="relative hidden lg:block">
                <div className="border border-[#E5E7EB] rounded-sm overflow-hidden relative shadow-sm">
                  <img loading="lazy"
                    src={heroImage}
                    alt={heroAlt}
                    className="w-full h-auto object-cover aspect-[16/9]"
                    data-testid={`${testIdPrefix}-hero-image`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#025979]/15 to-transparent pointer-events-none"></div>
                </div>
                <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm border border-[#025979]/20 rounded-sm px-2.5 py-1 flex items-center gap-1.5">
                  <Cpu className="w-3.5 h-3.5 text-[#025979]" strokeWidth={1.5} />
                  <span className="text-xs font-medium text-[#025979]">AI Powered</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PLATFORM MODULES --------------------------------------------- */}
        <section
          data-testid={`${testIdPrefix}-module-cards-section`}
          className="py-12 md:py-16"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2
              className="text-2xl sm:text-3xl font-semibold text-[#111827] tracking-tight mb-2"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              Platform Modules
            </h2>
            <p className="text-sm text-[#6B7280] mb-8">
              {modules.length} connected modules, built to work together across the warehouse lifecycle.
            </p>
            <div className={`grid ${gridCols} gap-6`}>
              {modules.map((mod) => {
                // Cards opt into navigation by setting `href`. Cards without
                // an href stay non-interactive (matches existing behaviour
                // for modules that don't yet have a dedicated page) so
                // SmartIntegrations only the SmartInspect tile becomes
                // clickable while Tcube / Futuroot remain unchanged.
                const interactive = !!mod.href;
                const goToModule = () => mod.href && navigate(mod.href);
                return (
                  <Card
                    key={mod.id}
                    data-testid={`${testIdPrefix}-module-card-${mod.id}`}
                    onClick={interactive ? goToModule : undefined}
                    role={interactive ? "link" : undefined}
                    tabIndex={interactive ? 0 : undefined}
                    onKeyDown={(e) => {
                      if (interactive && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        goToModule();
                      }
                    }}
                    className={`relative overflow-hidden bg-white border border-[#E5E7EB] border-t-4 border-t-[#025979] hover:border-[#025979]/60 hover:shadow-md shadow-sm transition-all duration-200 rounded-sm ${
                      interactive ? "cursor-pointer" : ""
                    }`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-center mb-3">
                        <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-[#E0F2FE]">
                          <mod.icon className="w-5 h-5 text-[#025979]" strokeWidth={1.5} />
                        </div>
                      </div>
                      <CardTitle
                        className="text-lg font-semibold text-[#111827]"
                        style={{ fontFamily: "Chivo, sans-serif" }}
                      >
                        {mod.title}
                      </CardTitle>
                      <CardDescription className="text-sm text-[#6B7280] mt-1.5 leading-relaxed">
                        {mod.description}
                      </CardDescription>
                    </CardHeader>
                    {interactive && (
                      <CardFooter className="pt-0">
                        <Button
                          data-testid={`${testIdPrefix}-module-btn-${mod.id}`}
                          variant="default"
                          onClick={(e) => { e.stopPropagation(); goToModule(); }}
                          className="w-full rounded-sm text-sm font-medium bg-[#025979] hover:bg-[#03738C] text-white shadow-sm"
                        >
                          Explore Platform
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </CardFooter>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
